import React, { useState } from 'react';
import classnames from 'classnames';
import classNames from 'classnames';
import { MeetingStatus } from '../../../../../api/meetings.dao';
import DefaultButton from '../../../../global/default-button/default-button';
import OperatorColumn from '../../../../global/operator-column/operator-column';
import { ConnectionType, IConnectionDetailsDTO, IRoomConnections, IRoomMeetingConnectionsDTO } from '../../../../../api/connections.dao';
import { ConfirmDeletePopover } from '../../shared/connections-list/_connection-delete-confirm-popover';
import { ConnectionListItem } from '../_connection-list-item';
import { MultiSelectOption } from '../../../../global/multi-select-list/_multi-select-option';
import { OperatorsListButtonWrapper } from '../../shared/operators-list-button/operators-list-button-wrapper';
import { IEntityRoleDTO } from '../../../../../api/events.dao';
import { useIntl } from 'react-intl';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../../tracking-service/tracking-values';
import KnovioPreviewLinkPopoverVM, { KnovioPreviewSourceType } from '../../shared/knovio-preview-link-popover/knovio-preview-link-popover.viewmodel';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import KnovioPreviewLinkPopover from '../../shared/knovio-preview-link-popover/knovio-preview-link-popover';
import { ConnectionsViewModel } from '../connections.viewmodel';
import { IUserOperatorDTO, OperatorRole } from '../../../../../api/operators.dao';
import { MeetingConnectionsListItemFilters } from './components/_meeting-connections-list-item-filters';
import { observer } from 'mobx-react';
import { MeetingApiParams } from '../../shared/connections-list/_connection-form/connection-form.viewmodel';
import { AttendeesListItem } from './components/_attendees-list-item';

interface IPropsMeetingConnectionsListItem {
    vm: ConnectionsViewModel;
    roomMeetingConnections: IRoomMeetingConnectionsDTO;
    roomConnections: IRoomConnections;
    isFirst: boolean;
    bulkOperatorsUpdateFormClosed: boolean;
    operatorRoles: IEntityRoleDTO[];
    eventTimezone: string;
    showDates: boolean;
    meetingParams: MeetingApiParams;
}

const MeetingConnectionsListItem = observer((props: IPropsMeetingConnectionsListItem) => {
    const [deletePopoverConnection, setDeletePopoverConnection] = useState<IConnectionDetailsDTO | null>(null);
    const intl = useIntl();
    const {
        vm,
        roomMeetingConnections,
        roomConnections,
        isFirst,
        bulkOperatorsUpdateFormClosed,
        operatorRoles,
        eventTimezone,
        showDates,
        meetingParams,
        roomMeetingConnections: {
            meetingId,
            operators,
            defaultConnection,
            meetingRecordEnabled,
            startDateTime,
            endDateTime,
            status,
        },
    } = props;

    const isSelected = () => vm.isEditFormShown(roomMeetingConnections.meetingId);

    const addOperatorsToTheMeeting = (operatopsList: IUserOperatorDTO[]): void => {
        vm.addOperatorsToTheMeeting(
            roomMeetingConnections.operators ?? [], operatopsList, roomMeetingConnections.meetingId, roomConnections.roomId
        );
    };

    const setOperatorRole = (email: string, role: OperatorRole): void => {
        vm.setOperatorRole(
            roomMeetingConnections.meetingId,
            roomConnections.roomId,
            email,
            role
        );
    };

    const removeOperator = (email: string): void => {
        vm.removeOperator(
            roomMeetingConnections.meetingId,
            roomConnections.roomId,
            email
        );
    };

    const handleTrackingEvent = (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CALL): void => {
        vm.trackConnectionsEventMixPanel(eventName);
    };

    const handleTroubleshoot = (connectionDetails: IConnectionDetailsDTO): void => {
        vm.handleTroubleshoot(connectionDetails, OpenFrom.Connections);
    };

    const trackHostJoinClick = (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS): void => {
        vm.trackHostJoinClick(eventName, OpenFrom.Connections);
    };

    const editMeetingConnection = (connection: IConnectionDetailsDTO): void => {
        if (vm.getKnovioLiveStreamModalVM() && connection.type === ConnectionType.Knovio) {
            vm.getKnovioLiveStreamModalVM().setMeetingStartDate(new Date(roomMeetingConnections.startDateTime));
        }

        vm.handleEditMeetingConnection(meetingId, connection, roomMeetingConnections);
    };

    const knovioPreviewLinkPopoverInstance = new KnovioPreviewLinkPopoverVM(KnovioPreviewSourceType.Connection);

    const attendeesList = {
        presenters: 'Presenter',
        participants: 'Participant',
        hosts: 'Host',
        supports: 'Support'
    };

    const connectionCanceledClass = classNames({ 'connection-list-item--cancelled': status === MeetingStatus.Cancelled });

    const onOpenKnovioPreviewLinkPopover = (connection: IConnectionDetailsDTO) => {
        if (connection?.type === ConnectionType.Knovio && hasPermission(Permission.PREVIEW_KNOVIO_CONNECTION)) {
            knovioPreviewLinkPopoverInstance.setConnectionInfo({
                ...meetingParams,
                meetingConnectionDetailsId: connection.meetingConnectionId
            });

            knovioPreviewLinkPopoverInstance.openPopover();
        }
    };

    const handleEditClick = () => {
        if (!vm.isUniqueJoiningLinksDeletionTriggerEnabled) return;

        vm.updateConnections(meetingId);
    };


    const handleConfigurePassportForm = (connection: IConnectionDetailsDTO): void => {
        vm.showConfigurePassportForm({
            accountId: vm.accountId,
            eventId: vm.event.eventId,
            meetingId: meetingId,
            meetingConnectionId: connection.meetingConnectionId,
        }, connection);
    };

    return (
        <tr className={classnames('meeting-list-item', connectionCanceledClass)} onDoubleClick={handleEditClick}>
            <td className={classnames({ 'merge-with-previous-row': !isFirst })} />

            <MeetingConnectionsListItemFilters
                startDateTime={startDateTime}
                endDateTime={endDateTime}
                eventTimezone={eventTimezone}
                showDates={showDates}
                meetingRecordEnabled={meetingRecordEnabled}
            />

            <td className={classnames('hoverable', 'p-right-0px', { 'active': isSelected() })}>
                {Object.keys(attendeesList).map(attKey => (roomMeetingConnections[attKey] || []).map(atts =>
                    <AttendeesListItem
                        key={`attendees-org-${attKey}-${atts.organizationId}`}
                        {...atts}
                        attendeeType={attendeesList[attKey]} />
                ))}
            </td>

            <td className={classnames('hoverable', 'p-left-0px', { 'active': isSelected() })}>
                {Object.keys(attendeesList).map(attKey => (roomMeetingConnections[attKey] || []).map(atts =>
                    <div className='table-list-layout' key={`connection-org-${attKey}-${atts.organizationId}`}>
                        <ConnectionListItem
                            connection={atts.connection}
                            eventTimezone={eventTimezone}
                            cloneConnection={vm.handleCloneConnection}
                            getInheritedConnection={() => vm.getInheritedConnection(
                                roomConnections,
                                attendeesList[attKey],
                                defaultConnection,
                                roomMeetingConnections
                            )}
                            setDeletePopoverConnection={setDeletePopoverConnection}
                            handleOnChange={(connection: IConnectionDetailsDTO) => editMeetingConnection(connection)}
                            showConfigurePassportForm={(connection: IConnectionDetailsDTO) => {
                                handleConfigurePassportForm(connection);
                            }}
                            webinarReportsVM={vm.getWebinarReportsVM()}
                            isOrg
                            knovioReplayConnection={vm.getKnovioReplayConnection(roomMeetingConnections)}
                            handleTroubleshoot={handleTroubleshoot}
                            handleTrackingEvent={handleTrackingEvent}
                            trackHostJoinClick={trackHostJoinClick}
                            meetingParams={meetingParams}
                            hideChevron
                            onOpenKnovioPreviewLinkPopover={onOpenKnovioPreviewLinkPopover}
                            shouldDisableDeleteButton={vm.shouldDisableDeleteButton(atts?.connection)}
                        />
                        {atts.attendees.map(att =>
                            <ConnectionListItem
                                connection={att.connection}
                                eventTimezone={eventTimezone}
                                orgConnection={atts.connection}
                                cloneConnection={vm.handleCloneConnection}
                                key={`connection-att-${att.attendeeId}`}
                                getInheritedConnection={() => vm.getInheritedConnection(
                                    roomConnections,
                                    attendeesList[attKey],
                                    defaultConnection,
                                    roomMeetingConnections
                                )}
                                setDeletePopoverConnection={setDeletePopoverConnection}
                                handleOnChange={(connection: IConnectionDetailsDTO) => editMeetingConnection(connection)}
                                showConfigurePassportForm={(connection: IConnectionDetailsDTO) => {
                                    handleConfigurePassportForm(connection);
                                }}
                                webinarReportsVM={vm.getWebinarReportsVM()}
                                knovioReplayConnection={vm.getKnovioReplayConnection(roomMeetingConnections)}
                                handleTroubleshoot={handleTroubleshoot}
                                handleTrackingEvent={handleTrackingEvent}
                                trackHostJoinClick={trackHostJoinClick}
                                meetingParams={meetingParams}
                                hideChevron
                                onOpenKnovioPreviewLinkPopover={onOpenKnovioPreviewLinkPopover}
                                shouldDisableDeleteButton={vm.shouldDisableDeleteButton(att?.connection)}
                            />
                        )}
                    </div>
                ))}
            </td>

            <td className={classnames('hoverable', { 'active': isSelected() })}>
                <MultiSelectOption
                    onClick={() => vm.handleSelectSingleMeeting(meetingId)}
                    isSelected={vm.isSingleMeetingSelected(roomMeetingConnections.meetingId)}
                    isDisabled={bulkOperatorsUpdateFormClosed}
                />
            </td>

            <td className={classnames('hoverable operators', { 'active': isSelected() })}>
                {roomConnections.operators
                    ? <OperatorColumn operators={roomConnections.operators ?? []} />
                    : null
                }
                <OperatorsListButtonWrapper
                    operators={operators}
                    initOperatorsData={vm.initOperatorsData}
                    addOperatorsToTheMeeting={addOperatorsToTheMeeting}
                    operatorsListToAdd={vm.operatorsListToAdd}
                    setOperatorRole={setOperatorRole}
                    removeOperator={removeOperator}
                    operatorRoles={operatorRoles}
                    intl={intl}
                />
            </td>

            <td className={classnames('schedule align-right hoverable', { 'active': isSelected() })}>
                <DefaultButton
                    className='visible-while-hovered'
                    modifier='slim'
                    text='Edit'
                    onClick={handleEditClick}
                    disabled={!vm.isUniqueJoiningLinksDeletionTriggerEnabled}
                />
            </td>

            <KnovioPreviewLinkPopover vm={knovioPreviewLinkPopoverInstance} />

            <ConfirmDeletePopover
                handleOnDelete={(
                    data, connectionDetails
                ) => vm.handleDeleteMeetingConnection(meetingId, data, null, connectionDetails)}
                deletePopoverConnection={deletePopoverConnection}
                setDeletePopoverConnection={setDeletePopoverConnection}
            />
        </tr>
    );
});

export { MeetingConnectionsListItem };